import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CheckCircle2, Trash2 } from 'lucide-react';

interface AgentFacilityRelationship {
  agentId: string;
  agentPhoneIdentifier: string | null;
  agentRetellId: string | null;
  agentDefaultForOrg: boolean | null;
  facilityId: string;
  facilityName: string | null;
  facilityEhrId: string;
  facilityIsPrivate: boolean | null;
}

interface AgentFacilityTableProps {
  relationships: AgentFacilityRelationship[];
  selectedRelationships: Set<string>;
  onRowSelect: (agentId: string, facilityId: string) => void;
  onSelectAll: (checked: boolean) => void;
  onDelete: (
    agentId: string,
    facilityId: string,
    agentPhone: string | null,
    facilityName: string | null
  ) => void;
  isLoading?: boolean;
}

export function AgentFacilityTable({
  relationships,
  selectedRelationships,
  onRowSelect,
  onSelectAll,
  onDelete,
  isLoading = false,
}: AgentFacilityTableProps) {
  const allSelected =
    relationships.length > 0 && selectedRelationships.size === relationships.length;

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox checked={allSelected} onCheckedChange={onSelectAll} />
            </TableHead>
            <TableHead>Agent</TableHead>
            <TableHead>Facility</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {relationships.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                {isLoading ? 'Loading relationships...' : 'No agent-facility relationships found'}
              </TableCell>
            </TableRow>
          ) : (
            relationships.map((relationship) => {
              const key = `${relationship.agentId}-${relationship.facilityId}`;
              return (
                <TableRow key={key}>
                  <TableCell>
                    <Checkbox
                      checked={selectedRelationships.has(key)}
                      onCheckedChange={() =>
                        onRowSelect(relationship.agentId, relationship.facilityId)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">
                        {relationship.agentPhoneIdentifier || 'No phone'}
                      </div>
                      {relationship.agentRetellId && (
                        <div className="text-sm text-muted-foreground">
                          Retell ID: {relationship.agentRetellId}
                        </div>
                      )}
                      {relationship.agentDefaultForOrg && (
                        <Badge variant="outline" className="text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">
                        {relationship.facilityName || 'Unnamed Facility'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        EHR ID: {relationship.facilityEhrId}
                      </div>
                      {relationship.facilityIsPrivate && (
                        <Badge variant="secondary" className="text-xs">
                          Private
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        onDelete(
                          relationship.agentId,
                          relationship.facilityId,
                          relationship.agentPhoneIdentifier,
                          relationship.facilityName
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
